// Field help — the close-time focus-restore fallback shared by both hosts
// (adversarial-review R3/M1/N2). The trap's own restore targets the Ask-AI
// control it captured as `prev` on mount; that control can be gone from the
// DOM by the time the trap's cleanup runs (a fill committed through the
// thread collapsed the row, or a conditional parent change hid the field
// entirely while the thread was open). Deferred so the check always runs
// strictly after every synchronous cleanup an unmount triggers — the trap's
// own restore attempt included — regardless of effect-declaration order
// between it and the host's own cleanup effect. The delay is deliberately
// past a single macrotask (not `setTimeout(…, 0)`): another surface can hold
// its own deferred refocus mid-flight at the same moment (e.g. the main
// composer's `requestAnimationFrame`-scheduled refocus after sending) —
// enough margin here means this check always runs LAST and wins, rather than
// racing it.
const FALLBACK_DELAY_MS = 50;

export const scheduleHelpFocusFallback = (fieldId: string): void => {
    window.setTimeout(() => {
        const overlay = document.querySelector('[data-testid="cf-form-overlay"]');
        const active = document.activeElement;
        // The trap's own restore already landed on something real, still
        // inside the modal boundary — nothing to do. Checking containment
        // (not just "is something focused") matters: a focused element left
        // over from something UNRELATED and earlier — e.g. the main
        // composer, which `MessageSender` refocuses after sending — can
        // still be `document.activeElement` at this point even though the
        // trap's own restore had nothing to land on; that's still a focus
        // escape past the trap's boundary and must be corrected, not treated
        // as a successful restore.
        if (overlay && active && active !== document.body && overlay.contains(active)) return;

        const row = document.querySelector<HTMLElement>(`[data-testid="cf-row-${fieldId}"]`);
        if (row) {
            row.focus();
            return;
        }

        // Last resort (N2b): the row doesn't exist at all — a conditional
        // parent change hid the field entirely while its thread was open.
        // Fall back to the overlay's own focus anchor: the same first-
        // focusable node its own trap already claims if nothing else has
        // focus 40ms after the overlay mounts.
        document.querySelector<HTMLElement>('[data-testid="cf-overlay-back"]')?.focus();
    }, FALLBACK_DELAY_MS);
};
